/**
 * useSaveDialogPrompt — the Save dialog as an awaitable call, for surfaces
 * whose save logic is already written as `await save(...)`.
 *
 *   const { promptSave, saveDialog } = useSaveDialogPrompt();
 *   const result = await promptSave({ suggestedName, mimeType, saver, writer });
 *   if (!result) return; // cancelled
 *   ...render {saveDialog} somewhere in the tree
 *
 * `writer` receives where the user chose (and runs again with Keep both or
 * Replace after a name clash); whatever the LAST successful call returned is
 * what `promptSave` resolves with. `getBytes` works too, for browser-held
 * bytes. Resolves `null` when the user cancels.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import type { VaultSaveTargetDto } from '@/lib/api';
import { SaveFileDialog, type SaveDialogSaver, type SaveFormat } from './SaveFileDialog';

export interface SavePromptOptions<T> {
  suggestedName: string;
  mimeType: string;
  saver: SaveDialogSaver;
  formats?: readonly SaveFormat[];
  /** The acting workspace; `null` = Personal. */
  initialHolderId?: string | null;
  /** A route that saves bytes it holds, where the user chose. Returns what
   *  the caller wants back, plus the saved file's id and name. */
  writer?: (
    target: VaultSaveTargetDto,
    format: SaveFormat | null,
  ) => Promise<T & { documentId: string; name: string }>;
  /** Or: bytes the browser holds (uploaded on the Save routes). */
  getBytes?: (format: SaveFormat | null) => Promise<Blob>;
}

interface Pending {
  options: SavePromptOptions<unknown>;
  resolve: (value: unknown) => void;
}

export function useSaveDialogPrompt(): {
  promptSave: <T>(
    options: SavePromptOptions<T>,
  ) => Promise<(T & { documentId: string; name: string }) | null>;
  saveDialog: ReactNode;
} {
  const [pending, setPending] = useState<Pending | null>(null);
  const lastWritten = useRef<unknown>(null);

  const promptSave = useCallback(
    <T,>(options: SavePromptOptions<T>) =>
      new Promise<(T & { documentId: string; name: string }) | null>((resolve) => {
        lastWritten.current = null;
        setPending({ options: options as SavePromptOptions<unknown>, resolve: resolve as never });
      }),
    [],
  );

  const settle = (value: unknown) => {
    pending?.resolve(value);
    setPending(null);
  };

  const options = pending?.options;
  const saveDialog = options ? (
    <SaveFileDialog
      open
      saver={options.saver}
      suggestedName={options.suggestedName}
      mimeType={options.mimeType}
      formats={options.formats}
      initialHolderId={options.initialHolderId ?? null}
      getBytes={options.getBytes}
      writer={
        options.writer
          ? async (target, format) => {
              const written = await options.writer!(target, format);
              lastWritten.current = written;
              return written;
            }
          : undefined
      }
      onSaved={({ document }) => settle(lastWritten.current ?? document)}
      onOpenChange={(open) => {
        if (!open) settle(null);
      }}
    />
  ) : null;

  return { promptSave, saveDialog };
}
